import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
	LexicalTypeaheadMenuPlugin,
	type QueryMatch,
	TypeaheadOption,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import type { TextNode } from "lexical";
import { useCallback, useMemo, useState } from "react";
import * as React from "react";
import * as ReactDOM from "react-dom";

import { $createHashtagNode } from "./HashtagNode";

import css from "./HashtagNodeStyles.module.scss";

// The character that triggers the hashtag popup.
const TRIGGER = "#";
// Chars we expect to see in a hashtag (non-space, non-punctuation).
const VALID_CHARS = "[^" + TRIGGER + "\\s]";
// At most, 10 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 10;

const HashtagRegex = new RegExp("(^|\\s|\\()(" + "[" + TRIGGER + "]" + "((?:" + VALID_CHARS + "){0,})" + ")$");

function checkForHashtags(text: string): QueryMatch | null {
	const match = HashtagRegex.exec(text);

	if (match !== null) {
		const leadingWhiteSpace = match[1];
		const matchingString = match[3];
		return {
			leadOffset: match.index + leadingWhiteSpace.length,
			matchingString,
			replaceableString: match[2],
		};
	}
	return null;
}

class HashtagTypeaheadOption extends TypeaheadOption {
	name: string;

	constructor(name: string) {
		super(name);
		this.name = name;
	}
}

const HashtagTypeaheadMenuItem = ({
	index,
	isSelected,
	onClick,
	onMouseEnter,
	option,
}: {
	index: number;
	isSelected: boolean;
	onClick: () => void;
	onMouseEnter: () => void;
	option: HashtagTypeaheadOption;
}) => {
	let className = css.item;
	if (isSelected) {
		className += ` ${css.selected}`;
	}

	return (
		<li
			key={option.key}
			tabIndex={-1}
			className={className}
			ref={option.setRefElement}
			role="option"
			aria-selected={isSelected}
			id={"typeahead-item-" + index}
			onMouseEnter={onMouseEnter}
			onClick={onClick}
		>
			<span className={css.text}>{option.name}</span>
		</li>
	);
};

type HashtagSuggestionPluginProps = {
	hashTags: string[];
};

export const HashtagSuggestionPlugin = ({ hashTags }: HashtagSuggestionPluginProps): JSX.Element | null => {
	const [editor] = useLexicalComposerContext();
	const [queryString, setQueryString] = useState<string | null>(null);
	let results: string[] = [];

	const searchHashtags = (query: string) => {
		return hashTags?.filter((hashtag) => hashtag?.toLowerCase().includes(query?.toLowerCase()));
	};

	if (queryString !== null) {
		results = searchHashtags(queryString);
	}

	const options = useMemo(
		() => results?.map((result) => new HashtagTypeaheadOption(result)).slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
		[results]
	);

	const onSelectOption = useCallback(
		(selectedOption: HashtagTypeaheadOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
			editor.update(() => {
				const hashtagNode = $createHashtagNode("#" + selectedOption.name);
				if (nodeToReplace) {
					nodeToReplace.replace(hashtagNode);
				}
				hashtagNode.select();
				closeMenu();
			});
		},
		[editor]
	);

	return (
		<LexicalTypeaheadMenuPlugin<HashtagTypeaheadOption>
			onQueryChange={setQueryString}
			onSelectOption={onSelectOption}
			triggerFn={checkForHashtags}
			options={options}
			menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
				anchorElementRef && results?.length && anchorElementRef?.current
					? ReactDOM.createPortal(
							<div className={`${css.typeaheadPopover} ${css.hashtagsMenu}`}>
								<ul>
									{options?.map((option, i: number) => (
										<HashtagTypeaheadMenuItem
											index={i}
											isSelected={selectedIndex === i}
											onClick={() => {
												setHighlightedIndex(i);
												selectOptionAndCleanUp(option);
											}}
											onMouseEnter={() => {
												setHighlightedIndex(i);
											}}
											key={option.key}
											option={option}
										/>
									))}
								</ul>
							</div>,
							anchorElementRef.current
						)
					: null
			}
		/>
	);
};
